import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { useEffect, useState, useMemo } from 'react';
import { Navbar } from '@/components/feature/Navbar';
import { Footer } from '@/components/feature/Footer';
import { SEOHead } from '@/components/feature/SEOHead';
import { SchemaMarkup, articleSchema, breadcrumbSchema } from '@/components/feature/SchemaMarkup';
import { SITE_URL } from '@/components/feature/seoConfig';

const articleMap: Record<string, {
  title: string;
  description: string;
  date: string;
  readTime: number;
  category: string;
  imageUrl: string;
  sections: { key: string; type: 'h2' | 'h3' | 'p' | 'list' | 'faq' | 'table' | 'cta' }[];
}> = {
  'allchinabuy-spreadsheet-guide': {
    title: 'allchinabuySpreadsheetGuide',
    description: 'allchinabuySpreadsheetGuideDesc',
    date: '2026-08-25',
    readTime: 8,
    category: 'spreadsheet',
    imageUrl: 'https://readdy.ai/api/search-image?query=Modern%20spreadsheet%20data%20grid%20organized%20product%20catalog%20digital%20interface%20warm%20tones%20clean%20minimal%20aesthetic%2C%20professional%20ecommerce%20directory%2C%20soft%20lighting&width=1200&height=630&seq=article-spreadsheet-hero&orientation=landscape',
    sections: [
      { key: 'spreadsheetGuideIntro', type: 'p' },
      { key: 'whatIsSpreadsheetTitle', type: 'h2' },
      { key: 'whatIsSpreadsheetContent', type: 'p' },
      { key: 'whyUseSpreadsheetTitle', type: 'h2' },
      { key: 'whyUseSpreadsheetContent1', type: 'list' },
      { key: 'whyUseSpreadsheetContent2', type: 'list' },
      { key: 'whyUseSpreadsheetContent3', type: 'list' },
      { key: 'whyUseSpreadsheetContent4', type: 'list' },
      { key: 'whyUseSpreadsheetContent5', type: 'list' },
      { key: 'howToUseSpreadsheetTitle', type: 'h2' },
      { key: 'howToUseSpreadsheetContent', type: 'p' },
      { key: 'popularCategoriesTitle', type: 'h2' },
      { key: 'popularCategoriesContent', type: 'p' },
      { key: 'spreadsheetTipsTitle', type: 'h2' },
      { key: 'spreadsheetTipsContent1', type: 'list' },
      { key: 'spreadsheetTipsContent2', type: 'list' },
      { key: 'spreadsheetTipsContent3', type: 'list' },
      { key: 'spreadsheetTipsContent4', type: 'list' },
      { key: 'spreadsheetTipsContent5', type: 'list' },
      { key: 'comparisonTitle', type: 'h2' },
      { key: 'comparisonContent', type: 'p' },
      { key: 'faqQuestion1', type: 'faq' },
      { key: 'faqQuestion2', type: 'faq' },
      { key: 'faqQuestion3', type: 'faq' },
      { key: 'faqQuestion4', type: 'faq' },
      { key: 'conclusionTitle', type: 'h2' },
      { key: 'conclusionContent', type: 'p' },
      { key: 'ctaMainTitle', type: 'cta' },
    ],
  },
  'allchinabuy-review-2026': {
    title: 'allchinabuyReview2026',
    description: 'allchinabuyReview2026Desc',
    date: '2026-08-20',
    readTime: 6,
    category: 'review',
    imageUrl: 'https://readdy.ai/api/search-image?query=Quality%20review%20rating%20stars%20checkmark%20premium%20ecommerce%20experience%2C%20warm%20neutral%20tones%2C%20clean%20minimal%20product%20photography%20background%2C%20professional%20modern%20aesthetic&width=1200&height=630&seq=article-review-hero&orientation=landscape',
    sections: [
      { key: 'spreadsheetGuideIntro', type: 'p' },
      { key: 'whatIsSpreadsheetTitle', type: 'h2' },
      { key: 'whatIsSpreadsheetContent', type: 'p' },
      { key: 'comparisonTitle', type: 'h2' },
      { key: 'comparisonContent', type: 'p' },
      { key: 'conclusionTitle', type: 'h2' },
      { key: 'conclusionContent', type: 'p' },
      { key: 'ctaMainTitle', type: 'cta' },
    ],
  },
  'allchinabuy-shipping-cost': {
    title: 'allchinabuyShippingCost',
    description: 'allchinabuyShippingCostDesc',
    date: '2026-08-18',
    readTime: 7,
    category: 'shipping',
    imageUrl: 'https://readdy.ai/api/search-image?query=Global%20shipping%20delivery%20packages%20worldwide%20logistics%2C%20cargo%20boxes%20airplane%20truck%2C%20warm%20neutral%20tones%2C%20clean%20minimal%20professional%20ecommerce%20photography%2C%20modern%20aesthetic&width=1200&height=630&seq=article-shipping-hero&orientation=landscape',
    sections: [
      { key: 'spreadsheetGuideIntro', type: 'p' },
      { key: 'whatIsSpreadsheetTitle', type: 'h2' },
      { key: 'whatIsSpreadsheetContent', type: 'p' },
      { key: 'conclusionTitle', type: 'h2' },
      { key: 'conclusionContent', type: 'p' },
      { key: 'ctaMainTitle', type: 'cta' },
    ],
  },
  'allchinabuy-coupon': {
    title: 'allchinabuyCoupon',
    description: 'allchinabuyCouponDesc',
    date: '2026-08-15',
    readTime: 5,
    category: 'coupon',
    imageUrl: 'https://readdy.ai/api/search-image?query=Discount%20coupon%20sale%20tags%20vouchers%20special%20offer%20ecommerce%20promotion%2C%20warm%20golden%20tones%2C%20clean%20minimal%20professional%20product%20photography%2C%20modern%20premium%20aesthetic&width=1200&height=630&seq=article-coupon-hero&orientation=landscape',
    sections: [
      { key: 'spreadsheetGuideIntro', type: 'p' },
      { key: 'whatIsSpreadsheetTitle', type: 'h2' },
      { key: 'whatIsSpreadsheetContent', type: 'p' },
      { key: 'conclusionTitle', type: 'h2' },
      { key: 'conclusionContent', type: 'p' },
      { key: 'ctaMainTitle', type: 'cta' },
    ],
  },
  'allchinabuy-warehouse-guide': {
    title: 'allchinabuyWarehouseGuide',
    description: 'allchinabuyWarehouseGuideDesc',
    date: '2026-08-12',
    readTime: 6,
    category: 'warehouse',
    imageUrl: 'https://readdy.ai/api/search-image?query=Modern%20warehouse%20storage%20facility%20organized%20shelves%20logistics%20ecommerce%2C%20clean%20minimal%20industrial%20aesthetic%2C%20warm%20neutral%20tones%2C%20professional%20photography&width=1200&height=630&seq=article-warehouse-hero&orientation=landscape',
    sections: [
      { key: 'spreadsheetGuideIntro', type: 'p' },
      { key: 'whatIsSpreadsheetTitle', type: 'h2' },
      { key: 'whatIsSpreadsheetContent', type: 'p' },
      { key: 'conclusionTitle', type: 'h2' },
      { key: 'conclusionContent', type: 'p' },
      { key: 'ctaMainTitle', type: 'cta' },
    ],
  },
  'is-allchinabuy-legit': {
    title: 'isAllchinabuyLegit',
    description: 'isAllchinabuyLegitDesc',
    date: '2026-08-10',
    readTime: 5,
    category: 'legit',
    imageUrl: 'https://readdy.ai/api/search-image?query=Security%20shield%20trust%20badge%20verification%20safe%20ecommerce%20payment%2C%20warm%20neutral%20tones%2C%20clean%20minimal%20professional%20modern%20aesthetic%2C%20premium%20safety%20concept&width=1200&height=630&seq=article-legit-hero&orientation=landscape',
    sections: [
      { key: 'spreadsheetGuideIntro', type: 'p' },
      { key: 'whatIsSpreadsheetTitle', type: 'h2' },
      { key: 'whatIsSpreadsheetContent', type: 'p' },
      { key: 'conclusionTitle', type: 'h2' },
      { key: 'conclusionContent', type: 'p' },
      { key: 'ctaMainTitle', type: 'cta' },
    ],
  },
};

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation();
  const [activeToc, setActiveToc] = useState('');

  const article = slug ? articleMap[slug] : null;

  useEffect(() => {
    if (article) {
      window.scrollTo(0, 0);
    }
  }, [article]);

  const { tocItems, faqItems, contentSections } = useMemo(() => {
    if (!article) return { tocItems: [], faqItems: [], contentSections: [] };
    const tocItems = article.sections.filter((s) => s.type === 'h2').map((s) => s.key);
    const faqItems = article.sections.filter((s) => s.type === 'faq');
    // Group list items into ul/ol blocks
    const contentSections: { type: string; items: typeof article.sections }[] = [];
    let currentGroup: { type: string; items: typeof article.sections } | null = null;
    for (const section of article.sections) {
      if (section.type === 'list') {
        if (currentGroup && currentGroup.type === 'list') {
          currentGroup.items.push(section);
        } else {
          if (currentGroup) contentSections.push(currentGroup);
          currentGroup = { type: 'list', items: [section] };
        }
      } else {
        if (currentGroup) contentSections.push(currentGroup);
        currentGroup = { type: section.type, items: [section] };
      }
    }
    if (currentGroup) contentSections.push(currentGroup);
    return { tocItems, faqItems, contentSections };
  }, [article]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-foreground-950">Article Not Found</h1>
          <Link to="/" className="mt-4 inline-block text-primary-600 hover:text-primary-700">
            {t('backToHome')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      <SEOHead
        title={t(article.title)}
        description={t(article.description)}
        canonicalUrl={`${SITE_URL}/article/${slug}`}
        ogImage={article.imageUrl}
        article
        publishedDate={article.date}
        modifiedDate={article.date}
      />
      <SchemaMarkup
        schema={[
          articleSchema({
            headline: t(article.title),
            description: t(article.description),
            image: article.imageUrl,
            url: `${SITE_URL}/article/${slug}`,
            datePublished: article.date,
            dateModified: article.date,
          }),
          breadcrumbSchema([
            { name: t('breadcrumbHome'), path: '/' },
            { name: t(article.title), path: `/article/${slug}` },
          ]),
        ]}
      />
      <Navbar />

      {/* Breadcrumb */}
      <div className="w-full px-4 md:px-8 lg:px-12 pt-24 pb-4">
        <nav aria-label="breadcrumb" className="max-w-4xl mx-auto">
          <ol className="flex items-center gap-2 text-sm text-foreground-500">
            <li>
              <Link to="/" className="hover:text-primary-600 transition-colors">{t('breadcrumbHome')}</Link>
            </li>
            <li><i className="ri-arrow-right-s-line text-xs" /></li>
            <li>
              <span className="text-foreground-950 font-medium">{t(article.title)}</span>
            </li>
          </ol>
        </nav>
      </div>

      <article className="w-full px-4 md:px-8 lg:px-12 pb-16">
        <div className="max-w-4xl mx-auto">
          {/* Hero Image */}
          <div className="relative rounded-2xl overflow-hidden mb-8">
            <img
              src={article.imageUrl}
              alt={t(article.title)}
              className="w-full h-64 md:h-80 object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
              <span className="inline-block px-3 py-1 rounded-full bg-white/90 text-xs font-medium text-foreground-700 mb-3 capitalize">
                {article.category}
              </span>
              <h1
                className="text-2xl md:text-4xl font-bold text-white leading-tight"
                style={{ fontFamily: 'var(--font-heading)' }}
              >
                {t(article.title)}
              </h1>
            </div>
          </div>

          {/* Meta */}
          <div className="flex flex-wrap items-center gap-4 mb-8 text-sm text-foreground-500">
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line" />
              {t('publishedOn')}: {article.date}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-time-line" />
              {t('readingTime')}: {article.readTime} {t('minutesRead')}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-user-line" />
              {t('writtenBy')}: AllChinaBuy Team
            </span>
          </div>

          <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
            {/* TOC Sidebar */}
            <aside className="lg:w-64 lg:flex-shrink-0">
              <div className="lg:sticky lg:top-24 bg-white rounded-xl border border-background-200/70 p-5">
                <h3 className="text-sm font-semibold text-foreground-950 uppercase tracking-wider mb-4">
                  {t('tableOfContents')}
                </h3>
                <ul className="space-y-2">
                  {tocItems.map((key) => (
                    <li key={key}>
                      <button
                        onClick={() => {
                          setActiveToc(key);
                          document.getElementById(key)?.scrollIntoView({ behavior: 'smooth' });
                        }}
                        className={`text-sm transition-colors ${
                          activeToc === key
                            ? 'text-primary-600 font-medium'
                            : 'text-foreground-600 hover:text-primary-600'
                        }`}
                      >
                        {t(key)}
                      </button>
                    </li>
                  ))}
                  {faqItems.length > 0 && (
                    <li>
                      <button
                        onClick={() => document.getElementById('faq')?.scrollIntoView({ behavior: 'smooth' })}
                        className="text-sm text-foreground-600 hover:text-primary-600 transition-colors"
                      >
                        FAQ
                      </button>
                    </li>
                  )}
                </ul>

                <div className="mt-6 pt-4 border-t border-background-200/60">
                  <Link
                    to="/"
                    className="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1"
                  >
                    <i className="ri-arrow-left-line" />
                    {t('backToHome')}
                  </Link>
                </div>
              </div>
            </aside>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="prose prose-lg max-w-none">
                {contentSections.map((group, groupIndex) => {
                  if (group.type === 'h2') {
                    const section = group.items[0];
                    return (
                      <h2
                        key={section.key}
                        id={section.key}
                        className="text-xl md:text-2xl font-bold text-foreground-950 mt-10 mb-4"
                        style={{ fontFamily: 'var(--font-heading)' }}
                      >
                        {t(section.key)}
                      </h2>
                    );
                  }
                  if (group.type === 'p') {
                    const section = group.items[0];
                    return (
                      <p key={section.key} className="text-foreground-700 leading-relaxed mb-4">
                        {t(section.key)}
                      </p>
                    );
                  }
                  if (group.type === 'list') {
                    return (
                      <ul key={`list-group-${groupIndex}`} className="mb-4 space-y-2">
                        {group.items.map((item) => (
                          <li key={item.key} className="text-foreground-700 leading-relaxed flex items-start gap-2">
                            <i className="ri-check-line text-primary-500 mt-1 flex-shrink-0" />
                            <span>{t(item.key)}</span>
                          </li>
                        ))}
                      </ul>
                    );
                  }
                  if (group.type === 'faq') {
                    const section = group.items[0];
                    const answerKey = section.key.replace('Question', 'Answer');
                    return (
                      <div key={section.key} className="bg-background-100 rounded-xl p-5 mb-4">
                        <h3 className="font-semibold text-foreground-950 mb-2">{t(section.key)}</h3>
                        <p className="text-foreground-600 text-sm">{t(answerKey)}</p>
                      </div>
                    );
                  }
                  if (group.type === 'cta') {
                    const section = group.items[0];
                    return (
                      <div key={section.key} className="bg-primary-50 rounded-xl p-6 md:p-8 mt-10 text-center">
                        <h3 className="text-lg font-bold text-foreground-950 mb-2">{t('ctaMainTitle')}</h3>
                        <p className="text-foreground-600 mb-4">{t('ctaMainDesc')}</p>
                        <a
                          href="https://kaspreadsheet.com"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-block px-6 py-3 rounded-full bg-primary-500 text-white font-semibold text-sm hover:bg-primary-600 transition-colors"
                        >
                          {t('ctaMainButton')}
                        </a>
                      </div>
                    );
                  }
                  return null;
                })}
              </div>

              <div className="mt-8 pt-6 border-t border-background-200/70">
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors"
                >
                  <i className="ri-arrow-left-line" />
                  {t('backToHome')}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
}